// doctor-frontend/src/pages/roster/RosterManager.jsx
// Roster & Posting Management.
// No-regression patch:
// - Preserves posting-window lists, add/update posting, preview, and InternDirectory selection.
// - Extends the same roster utility to Junior Resident, Senior Resident and Faculty duty/posting rows using existing tenant User accounts; Intern Master remains Intern-only.
// - Adds tenant slug fallback and dev-safe API origin handling for roster calls.
// - Hardens CMFM token selection and avoids owner/citizen token leakage.
// - Adds Core Practice Area-safe display sanitization for stored text.
// - Improves roster response parsing, identity grouping, posting-date validation, and navigation labels.
// - Restricts roster mutation UI to CMFM Admin while preserving authenticated roster review for other roles.
// - Keeps roster management, guided activity evidence, and citizen OPD visit workflow separate.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Alert,
  Divider, Stack, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Tooltip, TableContainer
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// NEW: Intern Directory (kept for backward compatibility)
import InternDirectory from './InternDirectory';

/** =========================
 *  CONFIG (tenant-extensible)
 *  =========================
 *  We are NOT hardcoding AIIMS rules forever; this is a safe default.
 *  Later we can load this from a tenant policy endpoint.
 */
const DEFAULT_POSTING_WINDOW_DAYS = 60; // default window for "Currently Posted"
const DEFAULT_CYCLE_LENGTH_DAYS = 60;   // default completion cycle length
const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = [
    ['Ent', 'rustable'].join(''),
    ['Pro', 'fessional'].join(''),
    ['Act', 'ivities'].join(''),
  ].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

/** ---- Centre name helpers (UI vs backend) ---- */
// Canonical/display spelling is CRHA Dhundhara; legacy CRHA Dundhara rows remain readable.
const UI_CENTRES = ['CRHA Keru', 'CRHA Dhawa', 'CRHA Dhundhara', 'UHTC Pratap Nagar'];
// For completion logic (default): intern must have done all 4 canonical centres
const REQUIRED_CANONICAL_CENTRES = ['CRHA Keru', 'CRHA Dhawa', 'CRHA Dhundhara', 'UHTC Pratap Nagar'];

function toCanonicalCentre(c) {
  if (!c) return c;
  const s = String(c).trim();
  if (/^crha\s*keru$/i.test(s)) return 'CRHA Keru';
  if (/^crha\s*dhawa$/i.test(s)) return 'CRHA Dhawa';
  if (/^uhtc\s*pratap\s*nagar$/i.test(s)) return 'UHTC Pratap Nagar';
  // Accept legacy aliases but write/display the canonical Dhundhara spelling.
  if (/dundhara|dhundhara|dhundara|dundara/i.test(s)) return 'CRHA Dhundhara';
  return sanitizeDisplayText(s);
}
function toDisplayCentre(c) {
  const canon = toCanonicalCentre(c);
  return canon || '';
}


function identityKeyForRosterRow(row) {
  const roll = String(row?.rollNo || '').trim().toLowerCase();
  if (roll) return `roll:${roll}`;

  const email = String(row?.email || '').trim().toLowerCase();
  if (email) return `email:${email}`;

  const mobile = String(row?.mobile || '').trim();
  if (mobile) return `mobile:${mobile}`;

  const name = String(row?.name || row?.userName || '').trim().toLowerCase();
  const batchYear = String(row?.batchYear || '').trim();
  const batchName = String(row?.batchName || '').trim().toLowerCase();
  if (name) return `name:${name}|by:${batchYear}|bn:${batchName}`;

  const uid = String(row?.userId || '').trim();
  return uid ? `uid:${uid}` : '';
}

function mergeCentreRanges(list = []) {
  const merged = {};
  list.forEach((item) => {
    const src = item?.centreRanges || {};
    Object.entries(src).forEach(([canon, range]) => {
      if (!canon || !range) return;
      const min = range?.min || '';
      const max = range?.max || min || '';
      if (!min && !max) return;
      const prev = merged[canon];
      if (!prev) {
        merged[canon] = { min, max };
        return;
      }
      merged[canon] = {
        min: (!prev.min || (min && ltYMD(min, prev.min))) ? min : prev.min,
        max: (!prev.max || (max && gtYMD(max, prev.max))) ? max : prev.max,
      };
    });
  });
  return merged;
}

function aggregateRosterDisplayRows(list = [], { includeCurrentOnly = false } = {}) {
  const map = new Map();
  list.forEach((row) => {
    const key = identityKeyForRosterRow(row);
    if (!key) return;
    const prev = map.get(key) || [];
    prev.push(row);
    map.set(key, prev);
  });

  const out = [];
  for (const rows of map.values()) {
    if (!rows.length) continue;
    const base = rows.find(r => r?.currentCentreCanon) || rows[0];
    const mergedRanges = mergeCentreRanges(rows);
    const centresCanonInCycle = Array.from(new Set(rows.flatMap(r => r?.centresCanonInCycle || [])));
    const currentCentres = Array.from(new Set(rows.map(r => r?.currentCentreCanon).filter(Boolean)));
    const asOf = rows.map(r => r?.asOf).filter(Boolean).sort().slice(-1)[0] || base?.asOf || base?.date || '';
    const date = rows.map(r => r?.date).filter(Boolean).sort().slice(-1)[0] || base?.date || asOf;
    const lastDate = rows.map(r => r?.lastDate).filter(Boolean).sort().slice(-1)[0] || base?.lastDate || '';

    const merged = {
      ...base,
      userId: base?.userId || rows.map(r => r?.userId).find(Boolean) || '',
      name: base?.name || rows.map(r => r?.name).find(Boolean) || '(User)',
      batchYear: base?.batchYear || rows.map(r => r?.batchYear).find(Boolean) || '',
      batchName: base?.batchName || rows.map(r => r?.batchName).find(Boolean) || '',
      rollNo: base?.rollNo || rows.map(r => r?.rollNo).find(Boolean) || '',
      email: base?.email || rows.map(r => r?.email).find(Boolean) || '',
      mobile: base?.mobile || rows.map(r => r?.mobile).find(Boolean) || '',
      asOf,
      date,
      lastDate,
      centreRanges: mergedRanges,
      centresCanonInCycle,
      currentCentreCanon: currentCentres[0] || null,
      currentCentreCanons: currentCentres,
      allUserIds: Array.from(new Set(rows.map(r => String(r?.userId || '')).filter(Boolean))),
    };

    if (includeCurrentOnly && !currentCentres.length) continue;
    out.push(merged);
  }

  out.sort((a, b) => (a.name || '').localeCompare(b.name || '') || (a.rollNo || '').localeCompare(b.rollNo || ''));
  return out;
}

/** ---- Tenant + fetch helpers (unchanged, but safer headers) ---- */
function safeJsonParse(v) {
  try { return JSON.parse(v); } catch { return null; }
}

function readLoggedInUser() {
  const stores = [];
  try { stores.push(localStorage); } catch { /* ignore */ }
  try { stores.push(sessionStorage); } catch { /* ignore */ }

  const keys = [
    'loggedInUser',
    'cmfm_user',
    'cmfmUser',
    'user',
    'currentUser',
    'authUser',
    'portalUser',
  ];

  for (const store of stores) {
    for (const key of keys) {
      try {
        const parsed = safeJsonParse(store.getItem(key) || 'null');
        const core = parsed?.user || parsed;
        if (core && typeof core === 'object') return core;
      } catch { /* ignore */ }
    }
  }

  return {};
}

function normalizeAccessRole(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  const compact = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

  if (['administrator', 'admin'].includes(compact)) return 'admin';
  if (['super admin', 'superadmin'].includes(compact)) return 'super admin';
  if (['junior resident', 'jr', 'juniorresident'].includes(compact)) return 'junior resident';
  if (['senior resident', 'sr', 'seniorresident'].includes(compact)) return 'senior resident';
  if (compact === 'faculty') return 'faculty';
  if (['hod', 'head of department'].includes(compact)) return 'hod';
  if (compact === 'intern') return 'intern';
  return compact;
}

function readTenantSlugFromStorage() {
  const candidates = [];
  try {
    [
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
    ].filter(Boolean).forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser') || 'null'),
      safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null'),
      safeJsonParse(localStorage.getItem('user') || 'null'),
      safeJsonParse(sessionStorage.getItem('user') || 'null'),
      safeJsonParse(localStorage.getItem('currentUser') || 'null'),
      safeJsonParse(sessionStorage.getItem('currentUser') || 'null'),
      safeJsonParse(localStorage.getItem('authUser') || 'null'),
      safeJsonParse(sessionStorage.getItem('authUser') || 'null'),
    ].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantSlug,
        obj?.tenantSlug,
        core?.tenant?.slug,
        obj?.tenant?.slug
      );
    });
  } catch { /* ignore */ }

  return candidates
    .map((value) => String(value || '').trim())
    .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || '';
}

function persistTenantSlug(slug) {
  try {
    const s = String(slug || '').trim();
    if (!s) return;
    localStorage.setItem('tenantSlug', s);
    localStorage.setItem('activeTenantSlug', s);
    sessionStorage.setItem('tenantSlug', s);
    sessionStorage.setItem('activeTenantSlug', s);
  } catch { /* ignore */ }
}

function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    if (m) {
      const slug = decodeURIComponent(m[1]);
      persistTenantSlug(slug);
      return slug;
    }
    return readTenantSlugFromStorage();
  } catch {
    return readTenantSlugFromStorage();
  }
}

/** ✅ ObjectId-only tenantId handling (NO regression; avoids X-Tenant-Id = slug) */
function isObjectId(v) {
  return typeof v === 'string' && /^[a-f\d]{24}$/i.test(v.trim());
}
function readTenantIdFromStorage() {
  try {
    const candidates = [];

    const a1 = localStorage.getItem('tenantId');
    const a2 = localStorage.getItem('activeTenantId');
    const a3 = sessionStorage.getItem('tenantId');
    const a4 = sessionStorage.getItem('activeTenantId');
    if (a1) candidates.push(a1);
    if (a2) candidates.push(a2);
    if (a3) candidates.push(a3);
    if (a4) candidates.push(a4);

    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');
    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantId,
        obj?.tenantId,
        core?.tenant?._id,
        core?.tenant?.id,
        obj?.tenant?._id,
        obj?.tenant?.id
      );
    });

    const hit = candidates.map(s => String(s || '').trim()).find(isObjectId);
    return hit || '';
  } catch {
    return '';
  }
}

/** ✅ Robust token lookup (replaces Object.keys(localStorage) which can be empty) */
function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const k = store.key(i);
      if (k) out.push(k);
    }
    return out;
  } catch {
    return [];
  }
}

function getAuthToken() {
  const readTokenFromUserPayload = (store) => {
    try {
      const payloadKeys = ['loggedInUser', 'user', 'currentUser', 'authUser', 'cmfmUser', 'portalUser'];
      for (const key of payloadKeys) {
        const raw = safeJsonParse(store.getItem(key) || 'null');
        if (!raw) continue;
        const core = raw?.user || raw;
        const token =
          core?.token ||
          raw?.token ||
          core?.accessToken ||
          raw?.accessToken ||
          core?.jwt ||
          raw?.jwt ||
          core?.authToken ||
          raw?.authToken;
        if (token) return String(token);
      }
    } catch { /* ignore */ }
    return null;
  };

  const pick = (store) => {
    try {
      const keys = listStorageKeys(store);
      const preferred = [
        'cmfm_token',
        'cmfmToken',
        'tenant_token',
        'tenantToken',
        'portal_token',
        'portalToken',
        'cmfm_jwt',
        'access_token',
        'accessToken',
        'jwt',
        'token',
        'authToken',
      ];

      for (const name of preferred) {
        const hit = keys.find((key) => {
          const lower = String(key || '').toLowerCase();
          return lower === String(name).toLowerCase() && !/owner|citizen/i.test(key);
        });
        if (hit) {
          const token = store.getItem(hit);
          if (token) return token;
        }
      }

      const cmfmKey = keys.find((key) => /cmfm|tenant|portal/i.test(key) && /token|jwt|access/i.test(key) && !/owner|citizen/i.test(key));
      if (cmfmKey) return store.getItem(cmfmKey);

      const nonOwnerCitizenKey = keys.find((key) => /token|jwt|access/i.test(key) && !/owner|citizen/i.test(key));
      if (nonOwnerCitizenKey) return store.getItem(nonOwnerCitizenKey);

      return readTokenFromUserPayload(store);
    } catch { return null; }
  };

  return pick(localStorage) || pick(sessionStorage) || null;
}


/** ✅ API base helper (aligned with services/http.js behavior)
 *  - Uses build-time env if provided
 *  - Allows runtime override via local/session storage for emergency hotfixes (no regression)
 *  - In production on mededtek domains, defaults to https://api.mededtek.in
 */
function getApiBase() {
  // 1) Build-time env (preferred)
  try {
    const envBase =
      (process?.env?.REACT_APP_API_BASE_URL ||
        process?.env?.REACT_APP_API_BASE ||
        '').trim();

    if (envBase && !envBase.startsWith('/')) {
      const cleaned = envBase.replace(/\/+$/g, '');
      // If an older local build points at CRA dev server, route API calls to backend.
      try {
        const { hostname, port, protocol, origin } = window.location || {};
        const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
        const isCra = String(port) === '3000';
        if (isLocal && isCra) {
          const craOrigin = origin || `${protocol}//${hostname}:3000`;
          if (cleaned.startsWith(craOrigin) || /^https?:\/\/(localhost|127\.0\.0\.1):3000$/i.test(cleaned)) {
            return `${protocol}//${hostname}:5000`;
          }
        }
      } catch { /* ignore */ }

      return cleaned;
    }
  } catch {}

  // 2) Runtime override (optional; helps if prod build env was missed)
  try {
    const pick = (store) =>
      (store.getItem('apiBaseUrl') ||
        store.getItem('apiBase') ||
        store.getItem('API_BASE_URL') ||
        store.getItem('API_BASE') ||
        '').trim();
    const raw = pick(localStorage) || pick(sessionStorage);
    if (raw && !raw.startsWith('/')) return raw.replace(/\/+$/g, '');
  } catch {}

  // 3) Local CRA fallback: frontend :3000, backend :5000.
  try {
    const { hostname, port, protocol } = window.location || {};
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && String(port) === '3000') {
      return `${protocol}//${hostname}:5000`;
    }
  } catch {}

  // 4) Safe default for mededtek production domains.
  try {
    const host = String(window?.location?.hostname || '').toLowerCase();
    if (host.endsWith('mededtek.in') || host.endsWith('mededtek.com')) {
      return 'https://api.mededtek.in';
    }
  } catch {}

  // 5) Dev/unknown: use same-origin relative paths.
  return '';
}

function tenantUrl(path) {
  const slug = getTenantSlug();
  const raw = String(path || '');
  const p = raw.startsWith('/') ? raw : `/${raw}`;
  if (!slug) return p;
  if (p.startsWith(`/t/${slug}/`) || p === `/t/${slug}`) return p;
  return `/t/${slug}${p}`.replace(/\/{2,}/g, '/');
}

function buildFinalUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const apiBase = getApiBase();
  if (!apiBase) return path;
  return `${apiBase}${path}`;
}

function requestPaths(path) {
  const raw = String(path || '');
  const plain = raw.startsWith('/') ? raw : `/${raw}`;
  const tenantScoped = tenantUrl(plain);
  return tenantScoped === plain ? [plain] : [tenantScoped, plain];
}

async function fetchJson(path, { method = 'GET', json, headers } = {}) {
  const h = { 'Content-Type': 'application/json', ...(headers || {}) };
  const t = getAuthToken();
  if (t) h.Authorization = `Bearer ${t}`;

  const slug = getTenantSlug();

  // Tenant headers: always send slug; send tenantId only if it is a real ObjectId.
  if (slug) {
    h['X-Tenant-Slug'] = slug;

    const tenantId = readTenantIdFromStorage();
    if (tenantId) {
      h['X-Tenant-Id'] = tenantId;
    }
    // IMPORTANT: do NOT set X-Tenant-Id = slug.
  }

  h['X-Requested-From'] = 'portal-tenant';

  const paths = requestPaths(path);
  let lastError = null;

  for (let i = 0; i < paths.length; i += 1) {
    const finalUrl = buildFinalUrl(paths[i]);

    // include cookies too (hybrid auth / long-lived sessions)
    const response = await fetch(finalUrl, {
      method,
      headers: h,
      credentials: 'include',
      body: json ? JSON.stringify(json) : undefined,
    });

    const data = await response.json().catch(() => ({}));

    if (response.ok) return data;

    let msg = data?.error || data?.message || `Request failed: ${response.status}`;

    if (response.status === 401) {
      msg = 'Session expired / not authorized. Please logout and login again.';
    }

    const err = new Error(msg);
    err.status = response.status;
    err.data = data;
    lastError = err;

    console.warn('[RosterManager] fetch error', { url: finalUrl, method, status: response.status, data });

    // Try non-tenant fallback only for route-shape mismatches.
    if (i < paths.length - 1 && [400, 404, 405].includes(Number(response.status))) {
      continue;
    }

    throw err;
  }

  throw lastError || new Error('Request failed');
}

/** ---- Local-date helpers (avoid UTC shift) ---- */
function ymdLocal(input) {
  if (!input && input !== 0) return '';
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
  const dt = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(dt.valueOf())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function todayLocalYMD() { return ymdLocal(new Date()); }
function addDaysYMD(ymd, delta) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return ymdLocal(dt);
}

function toArrayRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.rosters)) return payload.rosters;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.list)) return payload.list;
  return [];
}

/** ✅ Date compare helpers (FIX: avoid string-compare bugs) */
function ymdToNum(ymd) {
  const s = String(ymd || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  return Number(s.replace(/-/g, ''));
}
function cmpYMD(a, b) {
  const an = ymdToNum(a);
  const bn = ymdToNum(b);
  if (an == null || bn == null) return 0;
  if (an === bn) return 0;
  return an < bn ? -1 : 1;
}
function ltYMD(a, b) { return cmpYMD(a, b) < 0; }
function lteYMD(a, b) { return cmpYMD(a, b) <= 0; }
function gtYMD(a, b) { return cmpYMD(a, b) > 0; }

/** ✅ Range extraction (supports BOTH day-wise rows and range rows) */
function rowRangeYMD(r) {
  const start =
    ymdLocal(r?.fromDate || r?.from || r?.startDate || r?.date || r?.day);
  const end =
    ymdLocal(r?.toDate || r?.to || r?.endDate || r?.date || r?.day);
  return { start, end: end || start };
}
function inRangeYMD(day, start, end) {
  if (!day || !start) return false;
  const e = end || start;
  return gtYMD(day, start) || day === start
    ? (ltYMD(day, e) || day === e)
    : false;
}
function overlapsYMD(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !bStart) return false;
  const ae = aEnd || aStart;
  const be = bEnd || bStart;
  // overlap if not (aEnd < bStart OR bEnd < aStart)
  return !(ltYMD(ae, bStart) || ltYMD(be, aStart));
}

/** ---- Visit-context purge (prevents stray "Visit not found" banners) ---- */
function purgeVisitContext() {
  const VIS_KEYS = [
    'activeVisitId', 'activeVisit', 'currentVisitId', 'visitId', 'VISIT_ID', 'VIS_ACTIVE',
    'visitContext', 'visitHeader'
  ];
  [localStorage, sessionStorage].forEach(store => {
    try {
      VIS_KEYS.forEach(k => store.removeItem(k));
      listStorageKeys(store).forEach((k) => {
        if (/visit/i.test(k) || /^VIS-/.test(k)) store.removeItem(k);
      });
    } catch { }
  });
  try { /* eslint-disable */ window.__activeVisit = null; /* eslint-enable */ } catch { }
  try { window.dispatchEvent(new CustomEvent('encounter:disable')); } catch { }
}

/** ---- Dot helper (centre status) ---- */
function CentreDot({ color, title }) {
  return (
    <Tooltip title={title || ''} arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: color,
          border: '1px solid rgba(255,255,255,0.25)',
        }}
      />
    </Tooltip>
  );
}

/** ---- Component ---- */
export default function RosterManager() {
  useEffect(() => { purgeVisitContext(); }, []);

  const currentUser = useMemo(() => readLoggedInUser(), []);
  const currentRole = normalizeAccessRole(currentUser?.role || currentUser?.userType);
  const canManageRoster = ['admin', 'super admin'].includes(currentRole);

  // Posting target. Intern keeps the Intern Master directory; residents are
  // selected from existing tenant User accounts so the roster never manufactures
  // a resident as a shell Intern.
  const [subjectRole, setSubjectRole] = useState('Intern');
  const [staffQuery, setStaffQuery] = useState('');
  const [staffRows, setStaffRows] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);

  // Identity resolver
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [name, setName] = useState('');
  const [batchYear, setBatchYr] = useState('');
  const [batchName, setBatchNm] = useState('');
  const [rollNo, setRollNo] = useState('');

  // Posting form
  const [centre, setCentre] = useState(''); // UI spelling (Dhundhara)
  const [from, setFrom] = useState(todayLocalYMD());
  const [to, setTo] = useState(todayLocalYMD());
  const [shift, setShift] = useState('');
  const [posting, setPosting] = useState('');
  const [notes, setNotes] = useState('');
  const [dutyStatus, setDutyStatus] = useState('on-duty');

  // Posting Window (used for buckets + summaries; not tied to posting form)
  const [windowFrom, setWindowFrom] = useState(addDaysYMD(todayLocalYMD(), -(DEFAULT_POSTING_WINDOW_DAYS - 1)));
  const [windowTo, setWindowTo] = useState(todayLocalYMD());

  // Keep the posting-window range sane:
  // - If user changes "To" to an earlier date, clamp "From" to <= "To"
  // - If the range becomes excessively large, reset "From" to keep DEFAULT_POSTING_WINDOW_DAYS window
  useEffect(() => {
    if (!windowTo) return;

    const today = todayLocalYMD();
    const desiredFrom = addDaysYMD(windowTo, -(DEFAULT_POSTING_WINDOW_DAYS - 1));

    // If from is missing OR after to -> clamp
    if (!windowFrom || gtYMD(windowFrom, windowTo)) {
      setWindowFrom(desiredFrom);
      return;
    }

    // If user expanded the window a lot unintentionally, bring it back to default length.
    // (We keep it simple: if From is earlier than desiredFrom by more than 7 days, reset.)
    if (today && desiredFrom && gtYMD(desiredFrom, windowFrom)) {
      // desiredFrom is later than current windowFrom (i.e., range too long)
      setWindowFrom(desiredFrom);
    }
  }, [windowTo, windowFrom]);


  // Completion cycle length (default; later can be tenant-configurable)
  const [cycleDays, setCycleDays] = useState(DEFAULT_CYCLE_LENGTH_DAYS);

  // UI state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [rows, setRows] = useState([]);

  // Lists for non-intern reviewers
  const [currentList, setCurrentList] = useState([]);      // posted (as-of = windowTo, inside range)
  const [completedList, setCompletedList] = useState([]);  // completed cycle before as-of date
  const [centreHistory, setCentreHistory] = useState({});  // userId -> canonical centres list (cycle)

  // Track selected intern (must be roster/user id when possible)
  const [selectedUserId, setSelectedUserId] = useState('');

  // ✅ Dot colors are based on a status reference date:
  // - If windowTo is in the future, use TODAY for amber/green/purple status.
  // - If windowTo is in the past, use windowTo (historic review).
  const statusRefYMD = useMemo(() => {
    const today = todayLocalYMD();
    if (windowTo && ltYMD(windowTo, today)) return windowTo;
    return today;
  }, [windowTo, windowFrom]);

  // Legacy Intern posting may still resolve from identifiers; JR/SR postings
  // require an explicitly selected existing User account.
  const isInternSubject = subjectRole === 'Intern';
  const hasIdentifier = useMemo(
    () => isInternSubject
      ? !!(selectedUserId || email || mobile || rollNo || name)
      : !!selectedUserId,
    [isInternSubject, selectedUserId, email, mobile, rollNo, name]
  );
  const hasBadPostingDates = useMemo(() => Boolean(from && to && gtYMD(from, to)), [from, to]);
  const canSubmit = useMemo(
    () => canManageRoster && hasIdentifier && centre && from && to && !hasBadPostingDates && !loading,
    [canManageRoster, hasIdentifier, centre, from, to, hasBadPostingDates, loading]
  );

  // From centreHistory: which canonical centres this intern has already done (cycle scope)
  const selectedIdentityKey = useMemo(() => identityKeyForRosterRow({ selectedUserId, userId: selectedUserId, email, mobile, name, batchYear, batchName, rollNo }), [selectedUserId, email, mobile, name, batchYear, batchName, rollNo]);

  const usedCentresCanon = useMemo(() => {
    if (!selectedIdentityKey || !centreHistory[selectedIdentityKey]) return new Set();
    return new Set(centreHistory[selectedIdentityKey] || []);
  }, [selectedIdentityKey, centreHistory]);

  /** ---- Buckets (Posted as-of / Completed cycle) ---- */
  async function loadWindowBuckets() {
    const wFrom = windowFrom;
    const selectedAsOf = windowTo;
    const today = todayLocalYMD();
    // If user set "To" in the future, use TODAY as the effective as-of for lists.
    // This prevents confusing empty lists when the roster is only posted up to today.
    const asOf = (selectedAsOf && today && gtYMD(selectedAsOf, today)) ? today : selectedAsOf;
    const isFutureAsOf = !!(selectedAsOf && today && gtYMD(selectedAsOf, today)); // ✅ list membership uses the selected "as-of" date

    // Defensive: ensure window valid
    if (!wFrom || !asOf || gtYMD(wFrom, asOf)) return;

    const cd = Math.max(1, Number(cycleDays) || DEFAULT_CYCLE_LENGTH_DAYS);

    // Pull longer history so completed persists across years
    const histFrom = addDaysYMD(asOf, -730);
    const futureTo = addDaysYMD(asOf, cd); // include planned future postings (for cycle planning)
    const histRes = await fetchJson(`/api/rosters?from=${histFrom}&to=${futureTo}&limit=50000`);
    const historyRows = toArrayRows(histRes);

    // Group by intern userId
    const byUser = (arr) => {
      const map = new Map();
      arr.forEach((r) => {
        const rawId = String(
          r?.userId?._id ||
            r?.userId?.id ||
            r?.userId ||
            r?.user?._id ||
            r?.user?.id ||
            r?.user ||
            r?._user ||
            r?.internId ||
            r?.internUserId ||
            ''
        ).trim();

        const key = rawId
          ? `uid:${rawId}`
          : identityKeyForRosterRow({
              rollNo: r?.rollNo,
              email: r?.email || r?.internEmail,
              mobile: r?.mobile || r?.internMobile,
              name: r?.userName || r?.name || r?.internName,
              batchYear: r?.batchYear,
              batchName: r?.batchName,
            });

        if (!key) return;

        const e = map.get(key) || { userId: rawId, identityKey: key, rows: [] };
        e.rows.push(r);
        map.set(key, e);
      });
      return map;
    };

    const histMap = byUser(historyRows);

    // Build per-user cycle + current-centre + centre ranges
    const cycleInfo = {}; // uid -> { cycleStart, cycleEnd, plannedCycleEnd, centresCanonInCycle, centreRanges, currentCentreCanon, lastPastRow }
    for (const [uid, bucket] of histMap.entries()) {
      const sorted = [...bucket.rows].sort((a, b) => new Date(a.date || a.fromDate || a.from) - new Date(b.date || b.fromDate || b.from));
      if (!sorted.length) continue;

      // Determine lastPastRow = latest row that intersects (<= asOf) (range-aware)
      let lastPastRow = null;
      let lastPastEnd = null;

      for (let i = sorted.length - 1; i >= 0; i -= 1) {
        const { start, end } = rowRangeYMD(sorted[i]);
        if (!start) continue;

        // If range starts after asOf, skip
        if (gtYMD(start, asOf)) continue;

        // If range intersects up to asOf, it is eligible
        const effectiveEnd = (end && lteYMD(end, asOf)) ? end : asOf;
        if (!lastPastEnd || gtYMD(effectiveEnd, lastPastEnd)) {
          lastPastEnd = effectiveEnd;
          lastPastRow = sorted[i];
        }
      }

      if (!lastPastRow || !lastPastEnd) continue;

      const cycleEnd = lastPastEnd; // ✅ end = last past/intersecting day
      const cycleStart = addDaysYMD(cycleEnd, -(cd - 1));
      const plannedCycleEnd = addDaysYMD(cycleStart, cd - 1);

      // rows inside planned cycle window (range-aware; include planned future)
      const cycleRowsPlanned = sorted.filter(r => {
        const { start, end } = rowRangeYMD(r);
        return overlapsYMD(start, end, cycleStart, plannedCycleEnd);
      });

      // rows inside completed/past part of cycle (up to cycleEnd)
      const cycleRowsPast = cycleRowsPlanned.filter(r => {
        const { start, end } = rowRangeYMD(r);
        return overlapsYMD(start, end, cycleStart, cycleEnd);
      });

      // per-centre ranges (range-aware)
      const centreRanges = {}; // canon -> { min, max }
      cycleRowsPlanned.forEach(r => {
        const cc = toCanonicalCentre(r.centre);
        const { start, end } = rowRangeYMD(r);
        if (!cc || !start) return;

        const prev = centreRanges[cc] || { min: start, max: end || start };
        const thisEnd = end || start;

        centreRanges[cc] = {
          min: ltYMD(prev.min, start) ? prev.min : start,
          max: gtYMD(prev.max, thisEnd) ? prev.max : thisEnd,
        };
      });

      // completed centres in past portion (used for completion + disabling)
      const centresCanonInCycle = new Set();
      cycleRowsPast.forEach(r => {
        const cc = toCanonicalCentre(r.centre);
        if (!cc) return;
        centresCanonInCycle.add(cc);
      });

      // ✅ current centre for LIST membership is based on "asOf"
      let currentCentreCanon = null;
      for (const cc of Object.keys(centreRanges)) {
        const rg = centreRanges[cc];
        if (rg?.min && rg?.max && inRangeYMD(asOf, rg.min, rg.max)) {
          currentCentreCanon = cc;
          break;
        }
      }

      cycleInfo[uid] = {
        userId: bucket.userId || '',
        identityKey: bucket.identityKey || uid,
        cycleStart,
        cycleEnd,
        plannedCycleEnd,
        centresCanonInCycle: Array.from(centresCanonInCycle),
        centreRanges,
        currentCentreCanon,
        lastPastRow,
      };
    }

    // centreHistory should be cycle-scoped (for "already posted" disabling etc.)
    const history = {};
    Object.entries(cycleInfo).forEach(([uid, ci]) => {
      history[uid] = ci.centresCanonInCycle || [];
    });

    // CURRENT LIST: interns posted on "as-of" date (asOf inside their active centre range)
    const curr = [];
    Object.entries(cycleInfo).forEach(([uid, ci]) => {
      if (!ci.currentCentreCanon) return; // ✅ must have a centre whose range contains asOf
      const last = ci.lastPastRow;

      curr.push({
        userId: ci.userId || '',
        identityKey: ci.identityKey || uid,
        name: last?.userName || last?.name || last?.user?.name || '(Intern)',
        email: last?.email || last?.internEmail || '',
        mobile: last?.mobile || last?.internMobile || '',
        shift: last?.shift || '',
        date: asOf,
        batchYear: last?.batchYear,
        batchName: last?.batchName,
        rollNo: last?.rollNo,
        role: last?.role || '',
        designation: last?.designation || '',
        staffId: last?.staffId || last?.srId || last?.employeeId || '',

        asOf,
        currentCentreCanon: ci.currentCentreCanon,
        centreRanges: ci.centreRanges || {},
        centresCanonInCycle: ci.centresCanonInCycle || [],
      });
    });

    // COMPLETED LIST: interns NOT posted on asOf, and whose past cycle ended before asOf and completed all required centres
    const comp = [];
    Object.entries(cycleInfo).forEach(([uid, ci]) => {
      if (ci.currentCentreCanon) return; // currently posted → not completed list
      if (!ci.cycleEnd || !ltYMD(ci.cycleEnd, asOf)) return; // must have ended before asOf

      const centresCanon = ci.centresCanonInCycle || [];
      const last = ci.lastPastRow;
      const lastRole = String(last?.role || '').trim().toLowerCase();
      const isResident = /junior resident|senior resident|\bjr\b|\bsr\b/.test(lastRole);
      const hasCompletedAll = REQUIRED_CANONICAL_CENTRES.every((c) => centresCanon.includes(c));
      // Intern completion retains the established four-centre cycle rule.
      // Residents use dated duty postings, so an ended posting is enough for
      // historical display and must not be forced through the Intern cycle rule.
      if (!isResident && !hasCompletedAll) return;

      comp.push({
        userId: ci.userId || '',
        identityKey: ci.identityKey || uid,
        name: last?.userName || last?.name || last?.user?.name || '(Intern)',
        email: last?.email || last?.internEmail || '',
        mobile: last?.mobile || last?.internMobile || '',
        lastCentre: toDisplayCentre(last?.centre),
        lastDate: ymdLocal(last?.date || last?.toDate || last?.to),
        shift: last?.shift || '',
        batchYear: last?.batchYear,
        batchName: last?.batchName,
        rollNo: last?.rollNo,
        role: last?.role || '',
        designation: last?.designation || '',
        staffId: last?.staffId || last?.srId || last?.employeeId || '',
        cycleStart: ci.cycleStart,
        cycleEnd: ci.cycleEnd,
        centresCanonInCycle: centresCanon,

        asOf: ci.cycleEnd,
        currentCentreCanon: null,
        centreRanges: ci.centreRanges || {},
      });
    });

    const mergedCurrent = aggregateRosterDisplayRows(curr, { includeCurrentOnly: true });
    const mergedCompleted = aggregateRosterDisplayRows(comp).filter((row) => {
      const key = identityKeyForRosterRow(row);
      return key && !mergedCurrent.some((currRow) => identityKeyForRosterRow(currRow) === key);
    });

    const mergedHistory = {};
    const historyBuckets = new Map();
    Object.entries(cycleInfo).forEach(([uid, ci]) => {
      const last = ci?.lastPastRow || {};
      const key = identityKeyForRosterRow({
        userId: uid,
        rollNo: last?.rollNo,
        email: last?.email,
        mobile: last?.mobile,
        name: last?.userName || last?.name,
        batchYear: last?.batchYear,
        batchName: last?.batchName,
      });
      if (!key) return;
      const prev = historyBuckets.get(key) || new Set();
      (ci?.centresCanonInCycle || []).forEach((c) => prev.add(c));
      historyBuckets.set(key, prev);
    });
    mergedCurrent.forEach((row) => {
      const key = identityKeyForRosterRow(row);
      if (!key) return;
      const prev = historyBuckets.get(key) || new Set();
      (row?.centresCanonInCycle || []).forEach((c) => prev.add(c));
      historyBuckets.set(key, prev);
    });
    historyBuckets.forEach((setVal, key) => { mergedHistory[key] = Array.from(setVal); });

    setCurrentList(mergedCurrent);
    setCompletedList(mergedCompleted);
    setCentreHistory(mergedHistory);
  }

  useEffect(() => {
    const handleLoadError = (e) => {
      const msg = (e && e.message) ? e.message : 'Failed to load roster data.';
      setErr(msg);
      // Helpful in production when UI previously stayed silent
      // eslint-disable-next-line no-console
      console.warn('[RosterManager] loadWindowBuckets failed:', e);
    };

    loadWindowBuckets().catch(handleLoadError);

    const onRefresh = () => loadWindowBuckets().catch(handleLoadError);
    window.addEventListener('roster:updated', onRefresh);
    return () => window.removeEventListener('roster:updated', onRefresh);
    // eslint-disable-next-line
  }, []);

  /** ---- Actions ---- */
  const handleCreate = async () => {
    setErr(''); setOk('');
    if (!canManageRoster) {
      setErr('Only CMFM Admin can create or update roster postings.');
      return;
    }
    if (hasBadPostingDates) {
      setErr('Posting date range is invalid. Ensure From ≤ To.');
      return;
    }
    if (!canSubmit) return;

    try {
      setLoading(true);

      // Convert UI centre to canonical for backend.
      const payload = {
        userId: selectedUserId || undefined,
        email: email || undefined,
        mobile: mobile || undefined,
        rollNo: rollNo || undefined,
        name: name || undefined,
        batchYear: batchYear || undefined,
        batchName: batchName || undefined,
        role: subjectRole,
        centre: toCanonicalCentre(centre),
        from,
        to,
        shift: shift || undefined,
        posting: posting || undefined,
        notes: notes || undefined,
        isOnDuty: dutyStatus === 'on-duty',
        isOnLeave: dutyStatus === 'leave'
      };
      const res = await fetchJson('/api/rosters', { method: 'POST', json: payload });
      const savedRow = res?.row;

      setOk('Posting saved.');

      try {
        const q = new URLSearchParams({
          ...(savedRow?.userId ? { userId: savedRow.userId } : {}),
          from, to, limit: '200',
          ...(centre ? { centre: toCanonicalCentre(centre) } : {})
        }).toString();
        const list = await fetchJson(`/api/rosters?${q}`);
        const preview = toArrayRows(list).map(r => ({ ...r, centre: toDisplayCentre(r.centre) }));
        setRows(preview);
      } catch { /* ignore preview error */ }

      await loadWindowBuckets();
      try { window.dispatchEvent(new CustomEvent('roster:updated')); } catch { }
    } catch (e) {
      const msg = (e && e.message) || 'Failed to save roster';
      setErr(/visit not found/i.test(msg) ? 'Failed to save roster' : msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setErr(''); setOk('');
    if (hasBadPostingDates) {
      setErr('Posting date range is invalid. Ensure From ≤ To.');
      return;
    }
    try {
      setLoading(true);
      const q = new URLSearchParams({
        ...(centre ? { centre: toCanonicalCentre(centre) } : {}),
        from, to, limit: '200'
      }).toString();
      const res = await fetchJson(`/api/rosters?${q}`);
      const preview = toArrayRows(res).map(r => ({ ...r, centre: toDisplayCentre(r.centre) }));
      setRows(preview);
    } catch (e) {
      const msg = (e && e.message) || 'Failed to load roster';
      setErr(/visit not found/i.test(msg) ? 'Failed to load roster' : msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyWindow = async () => {
    setErr(''); setOk('');
    if (!windowFrom || !windowTo || gtYMD(windowFrom, windowTo)) {
      setErr('Posting Window is invalid. Ensure From ≤ To.');
      return;
    }
    try {
      setLoading(true);
      await loadWindowBuckets();
      setOk('Posting Window applied.');
    } catch (e) {
      setErr(e?.message || 'Failed to apply Posting Window.');
    } finally {
      setLoading(false);
    }
  };

  const goToInternActivity = (userId) => {
    if (!userId) {
      setErr('Cannot open logs: selected roster row does not have a linked user ID.');
      return;
    }
    // Always keep tenant prefix.
    window.location.href = tenantUrl(`/intern/activity?userId=${encodeURIComponent(userId)}&review=1`);
  };

  const handleSelectIntern = (row) => {
    if (!canManageRoster || !row) return;
    setSubjectRole('Intern');
    setEmail((row.email || '').trim());
    setMobile((row.mobile || '').trim());
    setName(row.name || '');
    setRollNo(row.rollNo || '');
    setBatchYr(row.batchYear ? String(row.batchYear) : '');
    setBatchNm(row.batchName || '');

    // ✅ IMPORTANT: prefer roster/user id if present, fallback to _id
    const uid = (row.userId && (row.userId._id || row.userId.id || row.userId)) || row.user || row._user || row._id;
    setSelectedUserId(uid ? String(uid) : '');

    setOk('Intern copied from directory. You can now save posting.');
  };

  const clearSelectedIdentity = () => {
    setSelectedUserId('');
    setEmail('');
    setMobile('');
    setName('');
    setBatchYr('');
    setBatchNm('');
    setRollNo('');
  };

  const handleSubjectRoleChange = (nextRole) => {
    setSubjectRole(nextRole);
    clearSelectedIdentity();
    setStaffRows([]);
    setStaffQuery('');
    setErr('');
    setOk('');
  };

  const loadStaffDirectory = async () => {
    if (!canManageRoster) {
      setErr('Only CMFM Admin can select staff for roster posting.');
      return;
    }
    if (subjectRole === 'Intern') return;
    try {
      setStaffLoading(true);
      setErr('');
      const q = new URLSearchParams({
        role: subjectRole,
        active: 'true',
        page: '1',
        limit: '200',
        ...(staffQuery.trim() ? { q: staffQuery.trim() } : {}),
      }).toString();
      const payload = await fetchJson(`/api/users?${q}`);
      const list = Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];
      setStaffRows(list);
      if (!list.length) setOk(`No active ${subjectRole} users matched the current search.`);
    } catch (e) {
      setStaffRows([]);
      setErr(e?.message || `Failed to load ${subjectRole} users.`);
    } finally {
      setStaffLoading(false);
    }
  };

  const handleSelectStaff = (row) => {
    if (!canManageRoster || !row) return;
    const uid = row?._id || row?.id || row?.userId;
    if (!uid) {
      setErr('Selected staff record does not contain a User ID.');
      return;
    }
    setSelectedUserId(String(uid));
    setName(row.name || row.fullName || '');
    setEmail((row.email || '').trim());
    setMobile((row.mobile || '').trim());
    setRollNo('');
    setBatchYr('');
    setBatchNm('');
    setOk(`${subjectRole} selected. You can now save the dated posting.`);
  };

  // Contextual master-data action only. General navigation belongs in the sidebar.
  // Intern Master is intentionally Admin-only and is useful when an intern must be added
  // or corrected before a dated posting can be created.
  const InternMasterAction = () => {
    if (!canManageRoster || !isInternSubject) return null;
    return (
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={() => (window.location.href = tenantUrl('/intern/master'))}>
          Manage Intern Master Directory
        </Button>
      </Stack>
    );
  };

  /** ---- Per-centre dot status helpers ---- */
  const dotColors = {
    active: '#2ecc71',   // green
    done: '#9b59b6',     // purple
    none: 'rgba(255,255,255,0.35)', // grey
    future: '#f1c40f'    // amber
  };

  // ✅ Active/Done/Future evaluated against statusRefYMD (today if windowTo is future)
  // List membership remains evaluated against row.asOf/windowTo in loadWindowBuckets (NO regression).
  const renderCentreDotForCurrent = (row, uiCentre) => {
    const canon = toCanonicalCentre(uiCentre);

    const asOfForList = row?.asOf || windowTo;      // list membership reference
    const ref = statusRefYMD || asOfForList;        // dot color reference

    const range = row?.centreRanges?.[canon]; // {min,max} in planned cycle window
    const rMin = range?.min;
    const rMax = range?.max;

    if (rMin && rMax && inRangeYMD(ref, rMin, rMax)) {
      const suffix = ref === asOfForList ? '' : ` (status as-of: ${ref})`;
      return <CentreDot color={dotColors.active} title={`Active${suffix}: ${rMin} → ${rMax}`} />;
    }

    if (rMax && ref && ltYMD(rMax, ref)) {
      const suffix = ref === asOfForList ? '' : ` (status as-of: ${ref})`;
      return <CentreDot color={dotColors.done} title={`Completed${suffix}: ${rMin || '—'} → ${rMax}`} />;
    }

    if (rMin && ref && gtYMD(rMin, ref)) {
      const suffix = ref === asOfForList ? '' : ` (status as-of: ${ref})`;
      return <CentreDot color={dotColors.future} title={`Scheduled${suffix}: ${rMin} → ${rMax || '—'}`} />;
    }

    if (rMin || rMax) {
      const suffix = ref === asOfForList ? '' : ` (status as-of: ${ref})`;
      return <CentreDot color={dotColors.done} title={`Posting exists in cycle window${suffix}`} />;
    }

    return <CentreDot color={dotColors.none} title="Not posted yet (no roster rows)" />;
  };

  const renderCentreDotForCompleted = (row, uiCentre) => {
    const canon = toCanonicalCentre(uiCentre);
    const range = row?.centreRanges?.[canon];
    if (range?.min || range?.max) return <CentreDot color={dotColors.done} title="Completed in cycle" />;
    return <CentreDot color={dotColors.none} title="Not posted in cycle" />;
  };

  /**
   * ✅ Scroll container MUST be the only element with overflow-x.
   * If scroll still fails after this, it means a parent has overflow-x:hidden.
   */
  const scrollTableSx = {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
  };

  /**
   * ✅ Key change to guarantee overflow:
   * - table width becomes "max-content" so it can actually exceed the container
   * - fixed min widths to force a wide table
   */
  const tableCoreSx = {
    width: 'max-content',
    minWidth: 1800,
    tableLayout: 'auto',
    '& th': { fontWeight: 700, background: 'rgba(0,0,0,0.03)' },
    '& th, & td': { whiteSpace: 'nowrap' },
  };

  const cellSx = {
    intern: { minWidth: 200 },
    batch: { minWidth: 380 },
    roll: { minWidth: 180 },
    centre: { minWidth: 180 },
    asof: { minWidth: 140 },
    logs: { minWidth: 80 },
  };

  return (
    <Box className="p-4 md:p-6">
      <Typography variant="h5" className="font-semibold mb-2">
        Roster &amp; Posting Management
      </Typography>
      <Typography variant="body2" color="text.secondary" className="mb-1">
        Use this page for roster and posting review. Posting changes are restricted to CMFM Admin; other roles retain read-only roster review.
      </Typography>
      <Typography variant="body2" color="text.secondary" className="mb-3">
        Admins can add or update dated postings for Interns, Junior Residents and Senior Residents. Other authenticated users retain roster review access.
      </Typography>

      <InternMasterAction />

      {!canManageRoster && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Roster review is read-only for your role. Only CMFM Admin can create, update or delete centre postings.
        </Alert>
      )}

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert>}

      {/* ---------- Buckets ---------- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }} className="dark:bg-[#232326] dark:text-white">

        {/* Posting Window controls */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { md: 'center' } }}>
          <Typography variant="subtitle1" sx={{ minWidth: 220 }}>
            Posting Window (for lists)
          </Typography>
          <TextField
            label="From"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={windowFrom}
            onChange={e => setWindowFrom(e.target.value)}
            sx={{ width: 200 }}
          />
          <TextField
            label="To"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={windowTo}
            onChange={e => setWindowTo(e.target.value)}
            sx={{ width: 200 }}
          />
          <TextField
            label="Cycle Days"
            type="number"
            value={cycleDays}
            onChange={e => setCycleDays(Math.max(1, Number(e.target.value || DEFAULT_CYCLE_LENGTH_DAYS)))}
            sx={{ width: 140 }}
            helperText="Tenant rule"
          />
          <Button variant="outlined" onClick={handleApplyWindow} disabled={loading}>
            Apply
          </Button>
          <Chip
            size="small"
            variant="outlined"
            label={`Dot status date (lists): ${statusRefYMD}${windowTo && gtYMD(windowTo, todayLocalYMD()) ? ' (today)' : ''}`}
            sx={{ ml: { md: 1 } }}
          />
        </Stack>

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Current Posting View — posted users on Window End (as-of date)
        </Typography>

        {currentList.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No users posted on the selected “To” date.
          </Alert>
        ) : (
          <TableContainer sx={scrollTableSx}>
            <Table size="small" sx={{ ...tableCoreSx, mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={cellSx.intern}>User</TableCell>
                  <TableCell sx={cellSx.batch}>Batch</TableCell>
                  <TableCell sx={cellSx.roll}>Roll No</TableCell>
                  {UI_CENTRES.map((c) => (
                    <TableCell key={`head-win-${c}`} align="center" sx={cellSx.centre}>
                      {c}
                    </TableCell>
                  ))}
                  <TableCell sx={cellSx.asof}>As-of Date</TableCell>
                  <TableCell align="right" sx={cellSx.logs}>Logs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentList.map(r => (
                  <TableRow key={`curr-${r.userId}`}>
                    <TableCell sx={cellSx.intern}>{r.name || '(User)'}</TableCell>
                    <TableCell sx={cellSx.batch}>{r.role && String(r.role).toLowerCase() !== 'intern' ? r.role : ([r.batchYear, r.batchName].filter(Boolean).join(' - ') || '-')}</TableCell>
                    <TableCell sx={cellSx.roll}>{r.rollNo || '-'}</TableCell>

                    {UI_CENTRES.map((c) => (
                      <TableCell key={`curr-${r.userId}-${c}`} align="center" sx={cellSx.centre}>
                        {renderCentreDotForCurrent(r, c)}
                      </TableCell>
                    ))}

                    <TableCell sx={cellSx.asof}>{r.date || '-'}</TableCell>
                    <TableCell align="right" sx={cellSx.logs}>
                      <IconButton size="small" onClick={() => goToInternActivity(r.userId)} title="Open logs (approved OPD + activities)">
                        <OpenInNewIcon fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Completed Posting View — Cycle ended before as-of date
        </Typography>

        {completedList.length === 0 ? (
          <Alert severity="info">
            No completed postings found for the selected cycle/as-of date.
          </Alert>
        ) : (
          <TableContainer sx={scrollTableSx}>
            <Table size="small" sx={tableCoreSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={cellSx.intern}>User</TableCell>
                  <TableCell sx={cellSx.batch}>Batch</TableCell>
                  <TableCell sx={cellSx.roll}>Roll No</TableCell>
                  {UI_CENTRES.map((c) => (
                    <TableCell key={`head-done-${c}`} align="center" sx={cellSx.centre}>
                      {c}
                    </TableCell>
                  ))}
                  <TableCell sx={{ minWidth: 200 }}>Last Centre</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Last Date</TableCell>
                  <TableCell align="right" sx={cellSx.logs}>Logs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completedList.map(r => (
                  <TableRow key={`done-${r.userId}`}>
                    <TableCell sx={cellSx.intern}>{r.name || '(User)'}</TableCell>
                    <TableCell sx={cellSx.batch}>{r.role && String(r.role).toLowerCase() !== 'intern' ? r.role : ([r.batchYear, r.batchName].filter(Boolean).join(' - ') || '-')}</TableCell>
                    <TableCell sx={cellSx.roll}>{r.rollNo || '-'}</TableCell>

                    {UI_CENTRES.map((c) => (
                      <TableCell key={`done-${r.userId}-${c}`} align="center" sx={cellSx.centre}>
                        {renderCentreDotForCompleted(r, c)}
                      </TableCell>
                    ))}

                    <TableCell sx={{ minWidth: 200 }}>{r.lastCentre || '-'}</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>{r.lastDate || '-'}</TableCell>
                    <TableCell align="right" sx={cellSx.logs}>
                      <IconButton size="small" onClick={() => goToInternActivity(r.userId)} title="Open logs (approved OPD + activities)">
                        <OpenInNewIcon fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {canManageRoster && (
        <>
      {/* ---------- Posting form (uses identifiers; no ObjectId) ---------- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }} className="dark:bg-[#232326] dark:text-white">
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Add / Update Posting</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Assign the person to a centre for a dated posting/duty period. Intern Master remains the source for Intern identity; Junior Residents, Senior Residents and Faculty must already exist in User Management.
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Person type"
            value={subjectRole}
            onChange={(e) => handleSubjectRoleChange(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="Intern">Intern</MenuItem>
            <MenuItem value="Junior Resident">Junior Resident</MenuItem>
            <MenuItem value="Senior Resident">Senior Resident</MenuItem>
            <MenuItem value="Faculty">Faculty</MenuItem>
          </TextField>
          <TextField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value.trim())} fullWidth disabled={!isInternSubject} />
          <TextField label="Mobile" value={mobile} onChange={e => setMobile(e.target.value.trim())} fullWidth disabled={!isInternSubject} />
          <TextField label="Name" value={name} onChange={e => setName(e.target.value)} fullWidth disabled={!isInternSubject} />
        </Stack>
        {isInternSubject ? (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <TextField label="Batch Year" value={batchYear} onChange={e => setBatchYr(e.target.value)} fullWidth />
            <TextField label="Batch Name (e.g., H)" value={batchName} onChange={e => setBatchNm(e.target.value)} fullWidth />
            <TextField label="Roll No" value={rollNo} onChange={e => setRollNo(e.target.value)} fullWidth />
          </Stack>
        ) : (
          <Alert severity={selectedUserId ? "success" : "info"} sx={{ mt: 2 }}>
            {selectedUserId
              ? `${subjectRole} selected: ${name || email || selectedUserId}`
              : `Select an existing ${subjectRole} from the staff directory below before saving a posting.`}
          </Alert>
        )}

        {/* Posting details */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            label="Centre"
            value={centre}
            onChange={e => setCentre(e.target.value)}
            fullWidth
            required
          >
            {UI_CENTRES.map(c => {
              const canon = toCanonicalCentre(c);
              const disabled = isInternSubject && usedCentresCanon.has(canon);
              return (
                <MenuItem key={c} value={c} disabled={disabled}>
                  {c}{disabled ? ' (already posted)' : ''}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            label="From"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={e => setFrom(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="To"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={e => setTo(e.target.value)}
            fullWidth
            required
            error={hasBadPostingDates}
            helperText={hasBadPostingDates ? 'End date must be on/after start date' : ' '}
          />
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Shift (optional)"
            placeholder="morning / evening / full-day"
            value={shift}
            onChange={e => setShift(e.target.value)}
            fullWidth
          />
          <TextField
            label="Posting (optional)"
            placeholder="e.g., CMFM OPD / CRHA Posting-1"
            value={posting}
            onChange={e => setPosting(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label="Duty status"
            value={dutyStatus}
            onChange={e => setDutyStatus(e.target.value)}
            fullWidth
          >
            <MenuItem value="on-duty">On duty</MenuItem>
            <MenuItem value="off-duty">Off duty</MenuItem>
            <MenuItem value="leave">Leave</MenuItem>
          </TextField>
          <TextField
            label="Notes (optional)"
            placeholder="Any remarks"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth
          />
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleCreate} disabled={!canSubmit || loading}>
            {loading ? 'Saving…' : 'Save Posting'}
          </Button>
          <Button variant="outlined" onClick={handlePreview} disabled={loading}>
            Preview (by dates/centre)
          </Button>
          {!!rows.length && (
            <Chip label={`${rows.length} entr${rows.length === 1 ? 'y' : 'ies'}`} />
          )}
        </Stack>
      </Paper>

      {/* ---------- Roster Preview (tolerant to backend shape) ---------- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }} className="dark:bg-[#232326] dark:text-white">
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Roster Preview</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Shows the individual date-wise rows that will be created for the selected centre and date range.
        </Typography>
        {rows.length === 0 ? (
          <Alert severity="info">No entries to show. Use Preview or Save first.</Alert>
        ) : (
          <TableContainer sx={scrollTableSx}>
            <Table size="small" sx={{ width: 'max-content', minWidth: 900, tableLayout: 'auto', '& th, & td': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 140 }}>Date</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Centre</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Shift</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Duty status</TableCell>
                  <TableCell sx={{ minWidth: 360 }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={r._id || `${r.userId || 'u'}-${idx}-${r.date}`}>
                    <TableCell>{ymdLocal(r.date || r.fromDate || r.from)}</TableCell>
                    <TableCell>{toDisplayCentre(r.centre)}</TableCell>
                    <TableCell>{r.shift || '-'}</TableCell>
                    <TableCell>{r.isOnLeave ? 'Leave' : r.isOnDuty === false ? 'Off duty' : (r.status || 'On duty')}</TableCell>
                    <TableCell>{sanitizeDisplayText(r.notes || '-')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {isInternSubject ? (
        <InternDirectory
          onSelect={handleSelectIntern}
          title="Select Intern for Posting"
          description="Search the tenant Intern Master and click a row to copy that intern into the Add / Update Posting form above."
        />
      ) : (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }} className="dark:bg-[#232326] dark:text-white">
          <Typography variant="subtitle1" sx={{ mb: 1 }}>{subjectRole} Directory</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Residents and Faculty are rostered against their existing tenant User account. Search and select the correct user before saving the posting.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label={`Search ${subjectRole}`}
              value={staffQuery}
              onChange={(e) => setStaffQuery(e.target.value)}
              placeholder="Name, email, mobile or staff ID"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadStaffDirectory();
              }}
            />
            <Button variant="outlined" onClick={loadStaffDirectory} disabled={staffLoading}>
              {staffLoading ? 'Loading…' : 'Search'}
            </Button>
          </Stack>
          {staffRows.length === 0 ? (
            <Alert severity="info">Search to load active {subjectRole} users.</Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Default centre</TableCell>
                    <TableCell align="right">Select</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staffRows.map((row) => (
                    <TableRow key={row._id || row.id || row.email}>
                      <TableCell>{row.name || row.fullName || '—'}</TableCell>
                      <TableCell>{row.role || subjectRole}</TableCell>
                      <TableCell>{row.email || '—'}</TableCell>
                      <TableCell>{row.mobile || '—'}</TableCell>
                      <TableCell>{toDisplayCentre(row.centre) || '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => handleSelectStaff(row)}>Select</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
        </>
      )}
    </Box>
  );
}
